import { Head, router, usePage } from "@inertiajs/react"
import {
    Autocomplete,
    Box,
    Button,
    Container,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import { DatePicker } from "@mui/x-date-pickers"
import dayjs, { Dayjs } from "dayjs"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { AuditEventGroup, MODULE_KEYS, ModuleKey } from "@/models/auditEvent"
import EventGroup from "./components/EventGroup"

type Filters = {
    module?: string
    actor?: string
    event?: string
    from?: string
    to?: string
}

type ActorOption = {
    identifier: string
    display_name: string | null
}

type PageProps = {
    groups: AuditEventGroup[]
    filters: Filters
    pagination: {
        current_page: number
        total_pages: number
        total_count: number
        per_page: number
    }
    modules: string[]
    actors: ActorOption[]
}

const EVENT_OPTIONS = ["create", "update", "destroy", "duplicate"] as const

function moduleI18nKey(key: ModuleKey): string {
    return ({
        employees: "employees",
        general_infos: "generalInfo",
        weekly_reports: "weeklyReport",
        payrolls: "payrolls",
        extrusions: "extrusion",
        orders: "orders",
        exports: "exports",
    } as const)[key]
}

export default function HistoryIndex() {
    const { props } = usePage<PageProps>()
    const { t } = useTranslation()

    const [moduleKey, setModuleKey] = useState<string>(props.filters.module ?? "")
    const [actor, setActor] = useState<string>(props.filters.actor ?? "")
    const [eventType, setEventType] = useState<string>(props.filters.event ?? "")
    const [from, setFrom] = useState<Dayjs | null>(props.filters.from ? dayjs(props.filters.from) : null)
    const [to, setTo] = useState<Dayjs | null>(props.filters.to ? dayjs(props.filters.to) : null)

    function applyFilters(extra: Partial<{ page: number }> = {}) {
        const query: Record<string, string | number> = {}
        if (moduleKey) query.module = moduleKey
        if (actor) query.actor = actor
        if (eventType) query.event = eventType
        if (from) query.from = from.format("YYYY-MM-DD")
        if (to) query.to = to.format("YYYY-MM-DD")
        if (extra.page) query.page = extra.page

        router.get("/history", query, { preserveState: false, preserveScroll: false })
    }

    function clearFilters() {
        setModuleKey("")
        setActor("")
        setEventType("")
        setFrom(null)
        setTo(null)
        router.get("/history")
    }

    function goBack() {
        // Skip the browser history (which may have stacked filter searches on
        // /history) and jump straight to the module the user came from. If
        // no module filter is active, go to the home page.
        const moduleKey = props.filters.module
        router.get(moduleKey ? `/${moduleKey}` : "/")
    }

    return (
        <>
            <Head title={t("history.title")} />
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: "center" }}>
                    <Tooltip title={t("common.back")}>
                        <IconButton onClick={goBack} size="small">
                            <ArrowBackIcon />
                        </IconButton>
                    </Tooltip>
                    <Typography variant="h4">{t("history.title")}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {t("history.subtitle")}
                </Typography>

                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Box
                        sx={{
                            display: "grid",
                            gap: 2,
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, 1fr)",
                                md: "repeat(3, 1fr)",
                                lg: "repeat(5, 1fr)",
                            },
                        }}
                    >
                        <FormControl size="small" fullWidth>
                            <InputLabel shrink>{t("history.filters.module")}</InputLabel>
                            <Select
                                value={moduleKey}
                                label={t("history.filters.module")}
                                onChange={e => setModuleKey(e.target.value)}
                                displayEmpty
                                notched
                            >
                                <MenuItem value="">{t("history.filters.all")}</MenuItem>
                                {MODULE_KEYS.map(key => (
                                    <MenuItem key={key} value={key}>
                                        {t(`modules.${moduleI18nKey(key)}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Autocomplete<ActorOption>
                            size="small"
                            fullWidth
                            options={props.actors}
                            value={props.actors.find(o => o.identifier === actor) ?? null}
                            onChange={(_, option) => setActor(option?.identifier ?? "")}
                            getOptionLabel={(option) => option.display_name || option.identifier}
                            isOptionEqualToValue={(a, b) => a.identifier === b.identifier}
                            renderInput={(params) => (
                                <TextField {...params} label={t("history.filters.actor")} />
                            )}
                        />

                        <FormControl size="small" fullWidth>
                            <InputLabel shrink>{t("history.filters.event")}</InputLabel>
                            <Select
                                value={eventType}
                                label={t("history.filters.event")}
                                onChange={e => setEventType(e.target.value)}
                                displayEmpty
                                notched
                            >
                                <MenuItem value="">{t("history.filters.all")}</MenuItem>
                                {EVENT_OPTIONS.map(ev => (
                                    <MenuItem key={ev} value={ev}>
                                        {t(`history.events.${ev}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <DatePicker
                            label={t("history.filters.from")}
                            value={from}
                            onChange={setFrom}
                            maxDate={dayjs()}
                            format="DD-MM-YYYY"
                            slotProps={{
                                textField: { size: "small", fullWidth: true },
                                field: { clearable: true, onClear: () => setFrom(null) },
                            }}
                        />
                        <DatePicker
                            label={t("history.filters.to")}
                            value={to}
                            onChange={setTo}
                            maxDate={dayjs()}
                            format="DD-MM-YYYY"
                            slotProps={{
                                textField: { size: "small", fullWidth: true },
                                field: { clearable: true, onClear: () => setTo(null) },
                            }}
                        />
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: "flex-end" }}>
                        <Button variant="text" onClick={clearFilters}>
                            {t("history.filters.clear")}
                        </Button>
                        <Button variant="contained" onClick={() => applyFilters()}>
                            {t("common.search")}
                        </Button>
                    </Stack>
                </Paper>

                {props.groups.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
                        <Typography color="text.secondary">{t("history.noEvents")}</Typography>
                    </Paper>
                ) : (
                    <Stack spacing={1}>
                        {props.groups.map(group => (
                            <EventGroup key={group.request_id} group={group} />
                        ))}
                    </Stack>
                )}

                {props.pagination.total_pages > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                        <Pagination
                            count={props.pagination.total_pages}
                            page={props.pagination.current_page}
                            onChange={(_, page) => applyFilters({ page })}
                        />
                    </Box>
                )}
            </Container>
        </>
    )
}
